import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StoryCard from "@/components/StoryCard";
import DivergenceBadge from "@/components/DivergenceBadge";
import { ArrowLeft, Layers, Flame, Loader2, Eye, Scale } from "lucide-react";
import { toast } from "sonner";

export default function TopicDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [stories, setStories] = useState([]);
  const [divergence, setDivergence] = useState(null);
  const [divLoading, setDivLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bookmarkIds, setBookmarkIds] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    api.get(`/topics/${id}`)
      .then((r) => {
        setTopic(r.data.topic);
        setStories(r.data.stories);
        setDivergence(r.data.divergence);
      })
      .catch(() => toast.error("Topic not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const runDivergence = async () => {
    setDivLoading(true);
    try {
      const r = await api.post(`/topics/${id}/divergence`);
      setDivergence(r.data);
      toast.success("Divergence analyzed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setDivLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { setBookmarkIds(new Set()); return; }
    api.get("/bookmarks/ids").then((r) => setBookmarkIds(new Set(r.data.ids))).catch(() => {});
  }, [user]);

  const toggleBookmark = async (storyId) => {
    const isB = bookmarkIds.has(storyId);
    try {
      if (isB) {
        await api.delete(`/bookmarks/${storyId}`);
        const n = new Set(bookmarkIds); n.delete(storyId); setBookmarkIds(n);
      } else {
        await api.post("/bookmarks", { story_id: storyId });
        const n = new Set(bookmarkIds); n.add(storyId); setBookmarkIds(n);
      }
    } catch { toast.error("Action failed"); }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading topic…
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center">
        <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500">Topic not found.</p>
        <Link to="/topics" className="font-mono-head text-xs uppercase tracking-widest text-[#E60000] mt-4 inline-block">← Back to topics</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/topics" className="inline-flex items-center gap-1 font-mono-head text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to topics
      </Link>

      <div className="border-b-2 border-stone-900 pb-8 mb-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-stone-900 bg-stone-200 px-2 py-1">
            <Layers className="w-3 h-3" /> {topic.count} outlets covering
          </span>
          <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-[#E60000]">
            <Flame className="w-3 h-3" /> Heat {topic.heat}
          </span>
          <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-500">· {topic.category}</span>
        </div>
        <h1 className="font-serif-head text-4xl sm:text-5xl text-stone-900 leading-tight mb-4">
          {topic.title}
        </h1>
        <div className="flex flex-wrap gap-1 mb-3">
          {topic.keywords.map((k) => (
            <span key={k} className="font-mono-head text-[10px] uppercase tracking-wider text-stone-700 border border-stone-300 px-2 py-1 bg-stone-100">
              {k}
            </span>
          ))}
        </div>
        <div className="font-mono-head text-[10px] uppercase tracking-widest text-stone-500">
          Sources: {topic.sources.join(" · ")}
        </div>
      </div>

      {/* Narrative Divergence */}
      <section data-testid="divergence-section" className="mb-10 border border-stone-300 bg-stone-100 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-[#E60000]" />
            <h3 className="font-mono-head text-sm uppercase tracking-[0.3em] text-stone-900">
              Narrative Divergence
            </h3>
          </div>
          {divergence ? (
            <DivergenceBadge divergence={divergence} size="lg" />
          ) : (
            <button
              data-testid="run-divergence-btn"
              onClick={runDivergence}
              disabled={divLoading}
              className="font-mono-head text-xs uppercase tracking-widest bg-stone-900 text-stone-50 px-4 py-2 hover:bg-[#E60000] transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {divLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              {divLoading ? "Analyzing…" : "Analyze Divergence"}
            </button>
          )}
        </div>

        {divergence ? (
          <>
            <p className="text-sm leading-relaxed text-stone-800 mb-4">
              {divergence.summary}
            </p>
            {divergence.framings && divergence.framings.length > 0 && (
              <div className="space-y-1">
                <div className="font-mono-head text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">
                  Per-outlet framing
                </div>
                {divergence.framings.map((f, i) => (
                  <div
                    key={`${f.outlet}-${i}`}
                    className="flex items-center gap-3 py-1.5 border-b border-stone-200 last:border-0"
                  >
                    <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-900 min-w-[140px]">
                      {f.outlet}
                    </span>
                    <span className="text-sm text-stone-700">{f.framing}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="font-mono-head text-xs uppercase tracking-widest text-stone-500">
            Compare how every outlet frames this same story · Powered by Claude Sonnet 4.5
          </p>
        )}
      </section>

      <h2 className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">
        All angles on this topic
      </h2>
      {stories.map((s, i) => (
        <StoryCard
          key={s.id}
          story={s}
          index={i}
          isBookmarked={bookmarkIds.has(s.id)}
          onToggleBookmark={toggleBookmark}
        />
      ))}
    </div>
  );
}
